import api from './api.ts'
import type { AxiosResponse } from 'axios'
import type {
    PromotionCriteria,
    StudentPromotionEvaluation,
    PromotionBatch,
    PromotionStatistics,
    BatchEvaluateRequest,
    EvaluationFilters,
    BulkApproveRequest,
    BulkRejectRequest,
    CriteriaReadiness
} from '@/types/promotion_schema'

const BASE_URL = '/promotions'

export interface CriteriaFilters {
    academic_year_id?: number
    grade_id?: number
    page?: number
    limit?: number
}

export interface CriteriaListResponse {
    criteria: PromotionCriteria[]
    count: number
    total: number
    page: number
    page_size: number
}

// ============================================================
// Criteria Management
// ============================================================

export const getCriteria = (params?: CriteriaFilters): Promise<AxiosResponse<{ data: CriteriaListResponse }>> => {
    return api.get(`${BASE_URL}/criteria`, { params })
}

export const createCriteria = (data: Partial<PromotionCriteria>): Promise<AxiosResponse<{ data: PromotionCriteria }>> => {
    return api.post(`${BASE_URL}/criteria`, data)
}

export const updateCriteria = (id: number, data: Partial<PromotionCriteria>): Promise<AxiosResponse<{ data: PromotionCriteria }>> => {
    return api.put(`${BASE_URL}/criteria/${id}`, data)
}

export const deleteCriteria = (id: number): Promise<AxiosResponse<void>> => {
    return api.delete(`${BASE_URL}/criteria/${id}`)
}

export const getCriteriaReadiness = (academicYearId: number): Promise<AxiosResponse<{ data: CriteriaReadiness }>> => {
    return api.get(`${BASE_URL}/criteria/readiness`, { params: { academic_year_id: academicYearId } })
}

// ============================================================
// Batch Operations
// ============================================================

export const batchEvaluate = (data: BatchEvaluateRequest): Promise<AxiosResponse<{ data: PromotionBatch }>> => {
    return api.post(`${BASE_URL}/batch/evaluate`, data)
}

export const getBatchStatus = (batchId: number): Promise<AxiosResponse<{ data: PromotionBatch }>> => {
    return api.get(`${BASE_URL}/batch/${batchId}`)
}

// ============================================================
// Evaluation Operations
// ============================================================

export const evaluateStudent = (
    studentId: number,
    academicYearId: number
): Promise<AxiosResponse<{ data: StudentPromotionEvaluation }>> => {
    return api.post(`${BASE_URL}/evaluate/${studentId}`, null, {
        params: { academic_year_id: academicYearId }
    })
}

export const getPendingEvaluations = (
    filters?: EvaluationFilters
): Promise<AxiosResponse<{ data: StudentPromotionEvaluation[], total: number }>> => {
    return api.get(`${BASE_URL}/pending`, { params: filters })
}

export const getEvaluations = (
    filters?: EvaluationFilters
): Promise<AxiosResponse<{ data: StudentPromotionEvaluation[], total: number }>> => {
    return api.get(`${BASE_URL}/evaluations`, { params: filters })
}

export const getStudentHistory = (
    studentId: number
): Promise<AxiosResponse<{ data: StudentPromotionEvaluation[] }>> => {
    return api.get(`${BASE_URL}/history/${studentId}`)
}

// ============================================================
// Approval Workflow
// ============================================================

export const approveEvaluation = (
    evaluationId: number
): Promise<AxiosResponse<{ data: StudentPromotionEvaluation }>> => {
    return api.post(`${BASE_URL}/approve/${evaluationId}`)
}

export const rejectEvaluation = (
    evaluationId: number,
    reason: string
): Promise<AxiosResponse<{ data: StudentPromotionEvaluation }>> => {
    return api.post(`${BASE_URL}/reject/${evaluationId}`, { reason })
}

export const bulkApprove = (
    data: BulkApproveRequest
): Promise<AxiosResponse<{ data: { approved_count: number } }>> => {
    return api.post(`${BASE_URL}/bulk-approve`, data)
}

export const bulkReject = (
    data: BulkRejectRequest
): Promise<AxiosResponse<{ data: { rejected_count: number } }>> => {
    return api.post(`${BASE_URL}/bulk-reject`, data)
}

// ============================================================
// Statistics
// ============================================================

export const getStatistics = (
    academicYearId: number
): Promise<AxiosResponse<{ data: PromotionStatistics }>> => {
    return api.get(`${BASE_URL}/statistics`, { params: { academic_year_id: academicYearId } })
}

export default {
    getCriteria,
    createCriteria,
    updateCriteria,
    deleteCriteria,
    getCriteriaReadiness,
    batchEvaluate,
    getBatchStatus,
    evaluateStudent,
    getPendingEvaluations,
    getEvaluations,
    getStudentHistory,
    approveEvaluation,
    rejectEvaluation,
    bulkApprove,
    bulkReject,
    getStatistics
}
